import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { Theme } from '@mui/material/styles';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    alpha,
    useTheme,
} from '@mui/material';
import { styled } from '@mui/system';
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SearchIcon from '@mui/icons-material/Search';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
    generateOrgSubscriptionReport,
    generateAthleteSubscriptionReport,
} from 'components/SubscriptionReport/generateSubscriptionReport';
import { getOrganizationsAndSubscriptionData } from '../../organizations/organizations.api';
import {
    Organization as OrgDashboardRow,
    Subscription,
} from '../../organizations/organizations.types';
import { getPods } from '../../pods/pods.api';
import { Pod } from '../../pods/pods.types';

// ── Styled ────────────────────────────────────────────────────────────────

const FilterBar = styled('form')(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
}));

// ── Types ─────────────────────────────────────────────────────────────────

type EntityMode = 'orgs' | 'athletes';

type AthleteStatusFilter = '' | 'active' | 'past_due' | 'no_sub' | 'comp';

// Augment Pod with a display-only assignment type
type AthletePod = Pod & { _assignment_type: 'transferred' | 'assigned' };

// ── Helpers ───────────────────────────────────────────────────────────────

const STATUS_SEVERITY: Record<string, number> = {
    past_due: 5,
    unpaid: 4,
    incomplete: 3,
    incomplete_expired: 3,
    paused: 2,
    trialing: 1,
    active: 0,
    canceled: 0,
    cancelled: 0,
};

function worstStripeStatus(subscriptions: Subscription[]): string | null {
    if (!subscriptions?.length) return null;
    return subscriptions.reduce<string | null>((worst, sub) => {
        if (!worst) return sub.stripe_status;
        const wSev = STATUS_SEVERITY[worst] ?? 0;
        const cSev = STATUS_SEVERITY[sub.stripe_status] ?? 0;
        return cSev > wSev ? sub.stripe_status : worst;
    }, null);
}

/**
 * Paid pod seat count from the org's subscriptions array (Stripe data via Cashier sync).
 * Includes past_due / unpaid / trialing subs — excludes only cancelled.
 * Returns null when no fiyrpod_organization sub is found so callers can
 * fall back to the API's org_pod_subscriptions field instead of showing 0.
 */
function orgPaidPodSeats(subscriptions: Subscription[]): number | null {
    if (!subscriptions?.length) return null;
    const podSubs = subscriptions.filter(
        (s) => s.name === 'fiyrpod_organization' && s.stripe_status !== 'canceled',
    );
    if (!podSubs.length) return null;
    return podSubs.reduce((total, sub) => {
        // items are not eager-loaded in the org list API; fall back to subscription.quantity
        const itemsQty = (sub.items ?? []).reduce((q, item) => q + (item.quantity ?? 0), 0);
        return total + (itemsQty || Number(sub.quantity ?? 0));
    }, 0);
}

/** Pick the primary subscription for a Stripe link (fiyrpod > organization > first). */
function primaryOrgSubscription(subscriptions: Subscription[]): Subscription | null {
    if (!subscriptions?.length) return null;
    return (
        subscriptions.find((s) => s.name === 'fiyrpod_organization') ??
        subscriptions.find((s) => s.name === 'organization') ??
        subscriptions[0] ??
        null
    );
}

function stripeStatusChip(status: string | null | undefined) {
    if (!status)
        return (
            <Typography variant="body2" color="text.disabled">
                --
            </Typography>
        );
    const colors: Record<string, 'success' | 'error' | 'warning' | 'default' | 'info'> = {
        active: 'success',
        trialing: 'info',
        past_due: 'error',
        unpaid: 'error',
        canceled: 'error',
        cancelled: 'error',
        incomplete: 'warning',
        incomplete_expired: 'warning',
        paused: 'warning',
    };
    const label = status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    return (
        <Chip
            label={label}
            size="small"
            color={colors[status] ?? 'default'}
            variant={status === 'active' ? 'filled' : 'outlined'}
            sx={{ fontWeight: 600, fontSize: '0.72rem' }}
        />
    );
}

function tierChip(tier: string | null | undefined) {
    if (!tier || tier === 'no_tier' || tier === '') {
        return (
            <Chip label="No Tier" size="small" variant="outlined" sx={{ fontSize: '0.72rem' }} />
        );
    }
    const colors: Record<string, string> = {
        gold: '#f59e0b',
        silver: '#6b7280',
        bronze: '#b45309',
    };
    const key = tier.toLowerCase();
    return (
        <Chip
            label={tier.charAt(0).toUpperCase() + tier.slice(1)}
            size="small"
            sx={{
                fontWeight: 600,
                fontSize: '0.72rem',
                borderColor: colors[key] ?? undefined,
                color: colors[key] ?? undefined,
            }}
            variant="outlined"
        />
    );
}

function formatDate(d: Date | string | null | undefined): string {
    if (!d) return '--';
    return new Date(d).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

function needsAttentionStripe(status: string | null | undefined): boolean {
    return status === 'past_due' || status === 'unpaid' || status === 'incomplete_expired';
}

function needsAttentionPod(status: string | null | undefined): boolean {
    return status === 'past_due';
}

// ── Summary Card ──────────────────────────────────────────────────────────

interface StatCardProps {
    label: string;
    value: string | number;
    color?: string;
    icon?: React.ReactNode;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, color, icon }) => (
    <Paper
        variant="outlined"
        sx={{
            px: 2.5,
            py: 1.5,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            minWidth: 140,
            borderColor: color ? alpha(color, 0.3) : 'divider',
            bgcolor: color ? alpha(color, 0.04) : 'transparent',
        }}
    >
        {icon}
        <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {label}
            </Typography>
        </Box>
    </Paper>
);

// ── Column label with info tooltip ────────────────────────────────────────

const ColHeader: React.FC<{ label: string; tip: string }> = ({ label, tip }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
        <span>{label}</span>
        <Tooltip title={tip} placement="top" arrow>
            <InfoOutlinedIcon sx={{ fontSize: '0.9rem', color: 'text.disabled', cursor: 'help' }} />
        </Tooltip>
    </Box>
);

// ── Org columns ───────────────────────────────────────────────────────────

function buildOrgColumns(
    theme: Theme,
    compPodsByOrg: Record<string, number>,
    loadingPdfId: string | null,
    onPdfClick: (row: OrgDashboardRow) => void,
): GridColDef<OrgDashboardRow>[] {
    return [
        {
            field: 'name',
            headerName: 'Organization',
            flex: 1,
            minWidth: 200,
            sortable: false,
            renderCell: (params) => {
                const stripeId = params.row.stripe_id;
                const status = worstStripeStatus(params.row.subscriptions ?? []);
                const warn = needsAttentionStripe(status);
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        {warn && (
                            <Tooltip title="Subscription needs attention">
                                <WarningAmberIcon
                                    sx={{ fontSize: '1rem', color: theme.palette.warning.main }}
                                />
                            </Tooltip>
                        )}
                        {stripeId ? (
                            <Typography
                                component="a"
                                href={`https://dashboard.stripe.com/customers/${stripeId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    color: theme.palette.primary.main,
                                    textDecoration: 'none',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.4,
                                    '&:hover': { textDecoration: 'underline' },
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {params.value}
                                <OpenInNewIcon sx={{ fontSize: '0.75rem', opacity: 0.6 }} />
                            </Typography>
                        ) : (
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {params.value}
                            </Typography>
                        )}
                    </Box>
                );
            },
        },
        {
            field: 'subscription_tier',
            headerName: 'Tier',
            width: 110,
            sortable: false,
            renderCell: (params) => tierChip(params.value),
        },
        {
            field: 'stripe_sub_link',
            headerName: 'Stripe Sub',
            width: 175,
            sortable: false,
            renderCell: (params) => {
                const sub = primaryOrgSubscription(params.row.subscriptions ?? []);
                const isPdfLoading = loadingPdfId === params.row.uuid;
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {sub?.stripe_id ? (
                            <Button
                                size="small"
                                variant="outlined"
                                component="a"
                                href={`https://dashboard.stripe.com/subscriptions/${sub.stripe_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                endIcon={<OpenInNewIcon sx={{ fontSize: '0.75rem !important' }} />}
                                sx={{
                                    fontSize: '0.72rem',
                                    py: 0.25,
                                    px: 1,
                                    minWidth: 0,
                                    textTransform: 'none',
                                }}
                            >
                                View
                            </Button>
                        ) : (
                            <Typography variant="body2" color="text.disabled" sx={{ width: 52 }}>
                                --
                            </Typography>
                        )}
                        <Tooltip title="Download subscription PDF report">
                            <span>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    disabled={isPdfLoading}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onPdfClick(params.row);
                                    }}
                                    startIcon={
                                        isPdfLoading ? (
                                            <CircularProgress size={10} />
                                        ) : (
                                            <PictureAsPdfIcon
                                                sx={{ fontSize: '0.8rem !important' }}
                                            />
                                        )
                                    }
                                    sx={{
                                        fontSize: '0.72rem',
                                        py: 0.25,
                                        px: 0.75,
                                        minWidth: 0,
                                        textTransform: 'none',
                                    }}
                                >
                                    PDF
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>
                );
            },
        },
        {
            field: 'org_pod_subscriptions',
            headerName: 'Sub Seats',
            width: 120,
            sortable: false,
            renderHeader: () => (
                <ColHeader
                    label="Sub Seats"
                    tip="Paid Stripe seats + comp pods. Hover for breakdown. Paid count reads directly from subscription quantity (includes past-due)."
                />
            ),
            renderCell: (params) => {
                // Paid: read from subscriptions array (includes past_due); fall back to API field
                const subsPaid = orgPaidPodSeats(params.row.subscriptions ?? []);
                const paid = subsPaid ?? Number(params.row.org_pod_subscriptions ?? 0);
                const comp = compPodsByOrg[params.row.uuid] ?? 0;
                const total = paid + comp;
                const tipParts: string[] = [];
                tipParts.push(`${paid} paid seat${paid !== 1 ? 's' : ''}`);
                if (comp > 0) tipParts.push(`${comp} comp pod${comp !== 1 ? 's' : ''}`);
                return (
                    <Tooltip title={tipParts.join(' · ')} placement="top" arrow>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'baseline',
                                gap: 0.5,
                                cursor: 'help',
                            }}
                        >
                            <Typography variant="body2">{total}</Typography>
                            {comp > 0 && (
                                <Typography variant="caption" color="text.secondary">
                                    ({paid}+{comp})
                                </Typography>
                            )}
                        </Box>
                    </Tooltip>
                );
            },
        },
        {
            field: 'pods_org',
            headerName: 'Actual Pods',
            width: 110,
            sortable: false,
            renderHeader: () => (
                <ColHeader
                    label="Actual Pods"
                    tip="Org pods not yet assigned to athletes + org pods assigned to athletes (org pays for both)"
                />
            ),
            renderCell: (params) => {
                const hasData = params.row.pods_org != null || params.row.pods_athlete != null;
                const total =
                    Number(params.row.pods_org ?? 0) + Number(params.row.pods_athlete ?? 0);
                return (
                    <Typography variant="body2" color={hasData ? 'text.primary' : 'text.disabled'}>
                        {hasData ? total : '--'}
                    </Typography>
                );
            },
        },
        {
            field: '_pod_match',
            headerName: 'Match?',
            width: 90,
            sortable: false,
            renderHeader: () => (
                <ColHeader
                    label="Match?"
                    tip="Actual pods vs total entitled (paid + comp). Green = covered."
                />
            ),
            renderCell: (params) => {
                const hasData = params.row.pods_org != null || params.row.pods_athlete != null;
                if (!hasData) {
                    return (
                        <Typography variant="body2" color="text.disabled">
                            --
                        </Typography>
                    );
                }
                const subsPaid = orgPaidPodSeats(params.row.subscriptions ?? []);
                const paid = subsPaid ?? Number(params.row.org_pod_subscriptions ?? 0);
                const comp = compPodsByOrg[params.row.uuid] ?? 0;
                const entitled = paid + comp;
                const actual =
                    Number(params.row.pods_org ?? 0) + Number(params.row.pods_athlete ?? 0);
                if (actual >= entitled) {
                    const tip =
                        actual === entitled
                            ? `${actual} pods = ${paid} paid + ${comp} comp`
                            : `${actual} pods ≥ ${entitled} entitled (${paid} paid + ${comp} comp)`;
                    return (
                        <Tooltip title={tip}>
                            <CheckCircleOutlineIcon
                                sx={{ fontSize: '1.1rem', color: 'success.main' }}
                            />
                        </Tooltip>
                    );
                }
                const short = entitled - actual;
                return (
                    <Tooltip
                        title={`${actual} pods < ${entitled} entitled (${paid} paid + ${comp} comp) — ${short} short`}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                            <WarningAmberIcon sx={{ fontSize: '1rem', color: 'warning.main' }} />
                            <Typography
                                variant="caption"
                                color="warning.main"
                                sx={{ fontWeight: 600 }}
                            >
                                {actual} / {entitled}
                            </Typography>
                        </Box>
                    </Tooltip>
                );
            },
        },
        {
            field: 'athlete_pod_subscriptions',
            headerName: 'Athlete Pod Subs',
            width: 140,
            sortable: false,
            type: 'number',
            renderHeader: () => (
                <ColHeader
                    label="Athlete Pod Subs"
                    tip="Count of pods transferred to individual athletes in this org"
                />
            ),
            renderCell: (params) => (
                <Typography variant="body2">
                    {params.value != null ? params.value : '--'}
                </Typography>
            ),
        },
        {
            field: 'pods_athlete',
            headerName: 'Athletes',
            width: 100,
            sortable: false,
            renderHeader: () => (
                <ColHeader
                    label="Athletes"
                    tip="Number of athletes with an org-assigned pod (one pod per athlete). Does not include transferred athletes."
                />
            ),
            renderCell: (params) => (
                <Typography variant="body2">
                    {params.value != null ? Number(params.value) : '--'}
                </Typography>
            ),
        },
        {
            field: 'uploads_last_30_days',
            headerName: 'Uploads 30d',
            width: 115,
            sortable: false,
            type: 'number',
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CloudUploadIcon
                        sx={{ fontSize: '0.85rem', color: theme.palette.info.main, opacity: 0.7 }}
                    />
                    <Typography variant="body2">{params.value ?? '--'}</Typography>
                </Box>
            ),
        },
        {
            field: 'uploads_last_60_days',
            headerName: 'Uploads 60d',
            width: 115,
            sortable: false,
            type: 'number',
            renderCell: (params) => <Typography variant="body2">{params.value ?? '--'}</Typography>,
        },
        {
            field: 'uploads_last_90_days',
            headerName: 'Uploads 90d',
            width: 115,
            sortable: false,
            type: 'number',
            renderCell: (params) => <Typography variant="body2">{params.value ?? '--'}</Typography>,
        },
        {
            field: 'amount_past_due',
            headerName: 'Past Due ($)',
            width: 115,
            sortable: true,
            renderCell: (params) => {
                const cents = params.value as number | null | undefined;
                if (cents == null || cents === 0) {
                    return (
                        <Typography variant="body2" color="text.disabled">
                            --
                        </Typography>
                    );
                }
                return (
                    <Typography variant="body2" color="error.main" sx={{ fontWeight: 600 }}>
                        ${(cents / 100).toFixed(2)}
                    </Typography>
                );
            },
        },
    ];
}

// ── Athlete (Pod) columns ─────────────────────────────────────────────────

function buildAthleteColumns(
    theme: Theme,
    loadingPdfId: string | null,
    onPdfClick: (row: AthletePod) => void,
): GridColDef<AthletePod>[] {
    return [
        {
            field: 'athlete_name',
            headerName: 'Athlete',
            flex: 1,
            minWidth: 200,
            sortable: true,
            renderCell: (params) => {
                const warn = needsAttentionPod(params.row.subscription_status);
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        {warn && (
                            <Tooltip title="Subscription past due">
                                <WarningAmberIcon
                                    sx={{ fontSize: '1rem', color: theme.palette.warning.main }}
                                />
                            </Tooltip>
                        )}
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {params.value || '--'}
                        </Typography>
                    </Box>
                );
            },
        },
        {
            field: 'serial_number',
            headerName: 'Serial #',
            width: 140,
            sortable: true,
            renderCell: (params) => (
                <Typography
                    variant="body2"
                    sx={{ fontFamily: 'monospace', fontSize: '0.8rem', letterSpacing: 0.3 }}
                >
                    {params.value || '--'}
                </Typography>
            ),
        },
        {
            field: '_assignment_type',
            headerName: 'Assignment',
            width: 130,
            sortable: true,
            renderCell: (params) => {
                const isTransferred = params.value === 'transferred';
                return (
                    <Chip
                        label={isTransferred ? 'Transferred' : 'Assigned'}
                        size="small"
                        color={isTransferred ? 'primary' : 'default'}
                        variant={isTransferred ? 'filled' : 'outlined'}
                        icon={<SwapHorizIcon sx={{ fontSize: '0.85rem !important' }} />}
                        sx={{ fontWeight: 600, fontSize: '0.72rem' }}
                    />
                );
            },
        },
        {
            field: 'subscription_status',
            headerName: 'Sub Status',
            width: 140,
            sortable: true,
            renderCell: (params) => {
                if (params.row.is_comp) {
                    return (
                        <Chip
                            label="Comp"
                            size="small"
                            color="info"
                            variant="outlined"
                            sx={{ fontWeight: 600, fontSize: '0.72rem' }}
                        />
                    );
                }
                if (params.row._assignment_type === 'assigned') {
                    return (
                        <Chip
                            label="Org Sub"
                            size="small"
                            color="default"
                            variant="outlined"
                            sx={{ fontWeight: 600, fontSize: '0.72rem' }}
                        />
                    );
                }
                return stripeStatusChip(params.value);
            },
        },
        {
            field: 'subscription_id',
            headerName: 'Stripe Sub',
            width: 175,
            sortable: false,
            renderCell: (params) => {
                const stripeSubId = params.value as string | null;
                const isPdfLoading = loadingPdfId === params.row.uuid;
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {stripeSubId ? (
                            <Button
                                size="small"
                                variant="outlined"
                                component="a"
                                href={`https://dashboard.stripe.com/subscriptions/${stripeSubId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                endIcon={<OpenInNewIcon sx={{ fontSize: '0.75rem !important' }} />}
                                sx={{
                                    fontSize: '0.72rem',
                                    py: 0.25,
                                    px: 1,
                                    minWidth: 0,
                                    textTransform: 'none',
                                }}
                            >
                                View
                            </Button>
                        ) : (
                            <Typography variant="body2" color="text.disabled" sx={{ width: 52 }}>
                                --
                            </Typography>
                        )}
                        <Tooltip title="Download subscription PDF report">
                            <span>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    disabled={isPdfLoading}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onPdfClick(params.row);
                                    }}
                                    startIcon={
                                        isPdfLoading ? (
                                            <CircularProgress size={10} />
                                        ) : (
                                            <PictureAsPdfIcon
                                                sx={{ fontSize: '0.8rem !important' }}
                                            />
                                        )
                                    }
                                    sx={{
                                        fontSize: '0.72rem',
                                        py: 0.25,
                                        px: 0.75,
                                        minWidth: 0,
                                        textTransform: 'none',
                                    }}
                                >
                                    PDF
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>
                );
            },
        },
        {
            field: 'organization_name',
            headerName: 'Organization',
            width: 180,
            sortable: true,
            renderCell: (params) => (
                <Typography variant="body2" color="text.secondary" noWrap title={params.value}>
                    {params.value || '--'}
                </Typography>
            ),
        },
        {
            field: 'recent_upload_count',
            headerName: 'Uploads (30d)',
            width: 120,
            sortable: true,
            type: 'number',
            renderCell: (params) => {
                const count = params.value as number;
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CloudUploadIcon
                            sx={{
                                fontSize: '0.85rem',
                                color: theme.palette.info.main,
                                opacity: 0.7,
                            }}
                        />
                        <Typography variant="body2">{count ?? '--'}</Typography>
                    </Box>
                );
            },
        },
        {
            field: 'updated_at',
            headerName: 'Updated',
            width: 130,
            sortable: true,
            renderCell: (params) => (
                <Typography variant="body2" color="text.secondary">
                    {formatDate(params.value)}
                </Typography>
            ),
        },
    ];
}

// ══════════════════════════════════════════════════════════════════════════
// ── Main Component ────────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════

const Fp1SubTrackingTab: React.FC = () => {
    const theme = useTheme();

    const [mode, setMode] = useState<EntityMode>('orgs');

    // ── PDF generation state ───────────────────────────────────────────────
    const [loadingPdfId, setLoadingPdfId] = useState<string | null>(null);

    const handleOrgPdf = useCallback(async (row: OrgDashboardRow) => {
        setLoadingPdfId(row.uuid);
        try {
            const status = worstStripeStatus(row.subscriptions ?? []);
            await generateOrgSubscriptionReport({
                orgUuid: row.uuid,
                orgName: row.name,
                ownerName:
                    row.owner_first_name && row.owner_last_name
                        ? `${row.owner_first_name} ${row.owner_last_name}`
                        : undefined,
                ownerEmail: row.owner_email,
                ownerPhone: row.owner_phone,
                subscriptionTier: row.subscription_tier,
                subscriptionStatus: status,
                stripeCustomerId: row.stripe_id,
            });
        } catch (err) {
            console.error('Failed to generate org PDF', err);
        } finally {
            setLoadingPdfId(null);
        }
    }, []);

    const handleAthletePdf = useCallback(async (row: AthletePod) => {
        setLoadingPdfId(row.uuid);
        try {
            await generateAthleteSubscriptionReport({
                athleteName: row.athlete_name ?? 'Unknown Athlete',
                serialNumber: row.serial_number,
                organizationName: row.organization_name,
                subscriptionStatus: row.subscription_status,
                subscriptionStartedAt: row.subscription_started_at,
                assignmentType:
                    row._assignment_type === 'transferred' ? 'Transferred' : 'Org Assigned',
            });
        } catch (err) {
            console.error('Failed to generate athlete PDF', err);
        } finally {
            setLoadingPdfId(null);
        }
    }, []);

    // ── Org state ──────────────────────────────────────────────────────────
    const [orgRows, setOrgRows] = useState<OrgDashboardRow[]>([]);
    const [orgTotal, setOrgTotal] = useState(0);
    const [orgPastDueTotal, setOrgPastDueTotal] = useState(0);
    const [orgLoading, setOrgLoading] = useState(false);
    const [orgPage, setOrgPage] = useState({ page: 0, pageSize: 25 });
    const [orgNameSearch, setOrgNameSearch] = useState('');
    const [orgTierFilter, setOrgTierFilter] = useState<
        '' | 'gold' | 'silver' | 'bronze' | 'no_tier'
    >('');
    const [orgPastDueOnly, setOrgPastDueOnly] = useState(false);
    const [orgSort, setOrgSort] = useState('-uploads_last_30_days');
    // comp pod counts keyed by org UUID — loaded once on mount from the pods table
    const [compPodsByOrg, setCompPodsByOrg] = useState<Record<string, number>>({});

    // ── Athlete (Pod) state — client-side pagination ───────────────────────
    const [allAthletePods, setAllAthletePods] = useState<AthletePod[]>([]);
    const [athleteLoading, setAthleteLoading] = useState(false);
    const [athletePage, setAthletePage] = useState({ page: 0, pageSize: 25 });
    const [athleteNameSearch, setAthleteNameSearch] = useState('');
    const [athleteStatusFilter, setAthleteStatusFilter] = useState<AthleteStatusFilter>('');
    // Applied filters (updated on Search button click)
    const [appliedAthleteNameFilter, setAppliedAthleteNameFilter] = useState('');
    const [appliedAthleteStatusFilter, setAppliedAthleteStatusFilter] =
        useState<AthleteStatusFilter>('');

    // ── Derived athlete data ───────────────────────────────────────────────
    const filteredAthletes = useMemo(() => {
        let rows = allAthletePods;
        if (appliedAthleteNameFilter.trim()) {
            const lower = appliedAthleteNameFilter.trim().toLowerCase();
            rows = rows.filter((p) => p.athlete_name?.toLowerCase().includes(lower));
        }
        if (appliedAthleteStatusFilter) {
            rows = rows.filter((p) => {
                switch (appliedAthleteStatusFilter) {
                    case 'active':
                        return p.has_active_subscription && !p.is_comp;
                    case 'past_due':
                        return p.subscription_status === 'past_due';
                    case 'no_sub':
                        return !p.has_active_subscription && !p.is_comp;
                    case 'comp':
                        return p.is_comp;
                    default:
                        return true;
                }
            });
        }
        return rows;
    }, [allAthletePods, appliedAthleteNameFilter, appliedAthleteStatusFilter]);

    const athletePastDueTotal = useMemo(
        () => allAthletePods.filter((p) => needsAttentionPod(p.subscription_status)).length,
        [allAthletePods],
    );

    // ── Fetch orgs ─────────────────────────────────────────────────────────
    const fetchOrgs = useCallback(
        async (
            page: number,
            pageSize: number,
            name: string,
            tier: string,
            pastDueOnly: boolean,
            sort: string,
        ) => {
            setOrgLoading(true);
            try {
                const res = await getOrganizationsAndSubscriptionData({
                    name: name || undefined,
                    tier: (tier as any) || 'all',
                    sort: sort as any,
                    page: page + 1,
                    per_page: pageSize,
                    is_problematic_account: pastDueOnly || undefined,
                });
                setOrgRows(res.data?.data ?? []);
                setOrgTotal(res.data?.meta?.total ?? 0);
            } catch (err) {
                console.error('Sub Tracking: failed to fetch orgs', err);
            } finally {
                setOrgLoading(false);
            }
        },
        [],
    );

    /** Background fetch to get the total past-due org count for the badge. */
    const fetchOrgPastDueTotal = useCallback(async () => {
        try {
            const res = await getOrganizationsAndSubscriptionData({
                is_problematic_account: true,
                per_page: 1,
                page: 1,
            });
            setOrgPastDueTotal(res.data?.meta?.total ?? 0);
        } catch {
            // non-critical
        }
    }, []);

    // ── Fetch comp pods for all orgs (once on mount) ──────────────────────
    const fetchCompPods = useCallback(async () => {
        try {
            const res = await getPods({ is_comp: true, per_page: 500, page: 1 });
            const counts: Record<string, number> = {};
            for (const pod of res.data?.data ?? []) {
                if (pod.organization_uuid) {
                    counts[pod.organization_uuid] = (counts[pod.organization_uuid] ?? 0) + 1;
                }
            }
            setCompPodsByOrg(counts);
        } catch {
            // non-critical; columns will show 0 comp if this fails
        }
    }, []);

    // ── Fetch athletes (both transferred + assigned, client-side pagination) ─
    const fetchAthletes = useCallback(async () => {
        setAthleteLoading(true);
        try {
            const [transferredRes, assignedRes] = await Promise.all([
                getPods({ pod_state: 'transferred', per_page: 500, page: 1 }),
                getPods({ pod_state: 'assigned', per_page: 500, page: 1 }),
            ]);
            const transferred: AthletePod[] = (transferredRes.data?.data ?? []).map((p) => ({
                ...p,
                _assignment_type: 'transferred' as const,
            }));
            const assigned: AthletePod[] = (assignedRes.data?.data ?? []).map((p) => ({
                ...p,
                _assignment_type: 'assigned' as const,
            }));
            setAllAthletePods([...transferred, ...assigned]);
        } catch (err) {
            console.error('Sub Tracking: failed to fetch athlete pods', err);
        } finally {
            setAthleteLoading(false);
        }
    }, []);

    // ── Initial load ───────────────────────────────────────────────────────
    useEffect(() => {
        fetchOrgs(0, 25, '', '', false, '-uploads_last_30_days');
        fetchOrgPastDueTotal();
        fetchCompPods();
        fetchAthletes();
    }, [fetchOrgs, fetchOrgPastDueTotal, fetchCompPods, fetchAthletes]);

    // ── Org search handler ─────────────────────────────────────────────────
    const handleOrgSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setOrgPage({ page: 0, pageSize: orgPage.pageSize });
        fetchOrgs(0, orgPage.pageSize, orgNameSearch, orgTierFilter, orgPastDueOnly, orgSort);
    };

    const handleOrgPageChange = (model: { page: number; pageSize: number }) => {
        setOrgPage(model);
        fetchOrgs(
            model.page,
            model.pageSize,
            orgNameSearch,
            orgTierFilter,
            orgPastDueOnly,
            orgSort,
        );
    };

    const handleOrgSortChange = (
        model: { field: string; sort: 'asc' | 'desc' | null | undefined }[],
    ) => {
        const item = model[0];
        const next = item
            ? `${item.sort === 'desc' ? '-' : ''}${item.field}`
            : '-uploads_last_30_days';
        setOrgSort(next);
        setOrgPage((p) => ({ ...p, page: 0 }));
        fetchOrgs(0, orgPage.pageSize, orgNameSearch, orgTierFilter, orgPastDueOnly, next);
    };

    // ── Athlete search handler ─────────────────────────────────────────────
    const handleAthleteSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setAthletePage({ page: 0, pageSize: athletePage.pageSize });
        setAppliedAthleteNameFilter(athleteNameSearch);
        setAppliedAthleteStatusFilter(athleteStatusFilter);
    };

    const gridSx = {
        border: 'none',
        '& .MuiDataGrid-cell:focus': { outline: 'none' },
        '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
        '& .MuiDataGrid-columnHeaders': { bgcolor: 'action.hover', borderRadius: 0 },
        '& .MuiDataGrid-row:hover': {
            bgcolor: (t: any) => alpha(t.palette.primary.main, 0.04),
        },
        '& .sub-tracking-row-warn': {
            bgcolor: (t: any) => alpha(t.palette.warning.main, 0.12),
        },
    };

    const orgColumns = buildOrgColumns(theme, compPodsByOrg, loadingPdfId, handleOrgPdf);
    const athleteColumns = buildAthleteColumns(theme, loadingPdfId, handleAthletePdf);

    return (
        <Box>
            {/* Mode toggle */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2.5,
                    flexWrap: 'wrap',
                    gap: 1.5,
                }}
            >
                <ToggleButtonGroup
                    value={mode}
                    exclusive
                    onChange={(_, v) => {
                        if (v) setMode(v);
                    }}
                    size="small"
                >
                    <ToggleButton
                        value="orgs"
                        sx={{ gap: 0.75, px: 2, textTransform: 'none', fontWeight: 600 }}
                    >
                        <BusinessIcon sx={{ fontSize: '1rem' }} />
                        Organizations
                    </ToggleButton>
                    <ToggleButton
                        value="athletes"
                        sx={{ gap: 0.75, px: 2, textTransform: 'none', fontWeight: 600 }}
                    >
                        <DirectionsRunIcon sx={{ fontSize: '1rem' }} />
                        Athletes
                    </ToggleButton>
                </ToggleButtonGroup>

                {/* Summary badges */}
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    {mode === 'orgs' && (
                        <>
                            <StatCard
                                label="Total Orgs"
                                value={orgTotal}
                                icon={
                                    <BusinessIcon
                                        sx={{
                                            color: theme.palette.primary.main,
                                            fontSize: '1.4rem',
                                        }}
                                    />
                                }
                                color={theme.palette.primary.main}
                            />
                            {orgPastDueTotal > 0 && (
                                <StatCard
                                    label="Past Due Orgs"
                                    value={orgPastDueTotal}
                                    icon={
                                        <WarningAmberIcon
                                            sx={{
                                                color: theme.palette.warning.main,
                                                fontSize: '1.4rem',
                                            }}
                                        />
                                    }
                                    color={theme.palette.warning.main}
                                />
                            )}
                        </>
                    )}
                    {mode === 'athletes' && (
                        <>
                            <StatCard
                                label="Total Athlete Pods"
                                value={allAthletePods.length}
                                icon={
                                    <DirectionsRunIcon
                                        sx={{
                                            color: theme.palette.info.main,
                                            fontSize: '1.4rem',
                                        }}
                                    />
                                }
                                color={theme.palette.info.main}
                            />
                            {athletePastDueTotal > 0 && (
                                <StatCard
                                    label="Past Due Athletes"
                                    value={athletePastDueTotal}
                                    icon={
                                        <WarningAmberIcon
                                            sx={{
                                                color: theme.palette.warning.main,
                                                fontSize: '1.4rem',
                                            }}
                                        />
                                    }
                                    color={theme.palette.warning.main}
                                />
                            )}
                        </>
                    )}
                </Box>
            </Box>

            {/* ═══ ORGS VIEW ═══ */}
            {mode === 'orgs' && (
                <>
                    <FilterBar onSubmit={handleOrgSearch}>
                        <TextField
                            size="small"
                            label="Search Name"
                            value={orgNameSearch}
                            onChange={(e) => setOrgNameSearch(e.target.value)}
                            sx={{ width: 220 }}
                            placeholder="Filter by org name..."
                        />
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel>Tier</InputLabel>
                            <Select
                                label="Tier"
                                value={orgTierFilter}
                                onChange={(e) => setOrgTierFilter(e.target.value as any)}
                            >
                                <MenuItem value="">All Tiers</MenuItem>
                                <MenuItem value="gold">Gold</MenuItem>
                                <MenuItem value="silver">Silver</MenuItem>
                                <MenuItem value="bronze">Bronze</MenuItem>
                                <MenuItem value="no_tier">No Tier</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    checked={orgPastDueOnly}
                                    onChange={(e) => setOrgPastDueOnly(e.target.checked)}
                                    color="warning"
                                />
                            }
                            label={
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    Past Due Only
                                </Typography>
                            }
                            sx={{ ml: 0.5, mr: 0 }}
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            startIcon={<SearchIcon />}
                            size="medium"
                        >
                            Search
                        </Button>
                    </FilterBar>

                    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        <DataGridPremium
                            rows={orgRows}
                            columns={orgColumns}
                            getRowId={(row) => row.uuid}
                            getRowClassName={(params) => {
                                const status = worstStripeStatus(params.row.subscriptions ?? []);
                                return needsAttentionStripe(status) ? 'sub-tracking-row-warn' : '';
                            }}
                            rowCount={orgTotal}
                            loading={orgLoading}
                            pagination
                            paginationMode="server"
                            paginationModel={orgPage}
                            onPaginationModelChange={handleOrgPageChange}
                            sortingMode="server"
                            onSortModelChange={handleOrgSortChange}
                            pageSizeOptions={[10, 25, 50]}
                            disableColumnMenu
                            checkboxSelection={false}
                            autoHeight
                            sx={gridSx}
                        />
                    </Paper>
                </>
            )}

            {/* ═══ ATHLETES VIEW ═══ */}
            {mode === 'athletes' && (
                <>
                    <FilterBar onSubmit={handleAthleteSearch}>
                        <TextField
                            size="small"
                            label="Search Athlete"
                            value={athleteNameSearch}
                            onChange={(e) => setAthleteNameSearch(e.target.value)}
                            sx={{ width: 220 }}
                            placeholder="Filter by name..."
                        />
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel>Subscription Status</InputLabel>
                            <Select
                                label="Subscription Status"
                                value={athleteStatusFilter}
                                onChange={(e) =>
                                    setAthleteStatusFilter(e.target.value as AthleteStatusFilter)
                                }
                            >
                                <MenuItem value="">All Statuses</MenuItem>
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="past_due">Past Due</MenuItem>
                                <MenuItem value="no_sub">No Subscription</MenuItem>
                                <MenuItem value="comp">Comp</MenuItem>
                            </Select>
                        </FormControl>
                        <Button
                            type="submit"
                            variant="contained"
                            startIcon={<SearchIcon />}
                            size="medium"
                        >
                            Search
                        </Button>
                    </FilterBar>

                    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        <DataGridPremium
                            rows={filteredAthletes}
                            columns={athleteColumns}
                            getRowId={(row) => row.uuid}
                            getRowClassName={(params) =>
                                needsAttentionPod(params.row.subscription_status)
                                    ? 'sub-tracking-row-warn'
                                    : ''
                            }
                            loading={athleteLoading}
                            pagination
                            paginationModel={athletePage}
                            onPaginationModelChange={(model) => setAthletePage(model)}
                            pageSizeOptions={[10, 25, 50, 100]}
                            disableColumnMenu
                            checkboxSelection={false}
                            autoHeight
                            sx={gridSx}
                        />
                    </Paper>
                </>
            )}
        </Box>
    );
};

export default Fp1SubTrackingTab;
